import { Analytics } from '@modules/commercetools/src/components/Analytics'
import React, {
  createContext,
  useContext,
  PropsWithChildren,
  useEffect,
} from 'react'

type Exposure = {
  name: string
  variant: string
}

declare global {
  interface WindowEventMap {
    expose: CustomEvent<Exposure>
  }
}

class ExposureEvent extends Event {
  constructor(
    private name: string,
    private variant: string
  ) {
    super('expose')
  }
  get detail(): Exposure {
    return {
      name: this.name,
      variant: this.variant,
    }
  }
}

const Provider = createContext<Record<string, string> | null>(null)

export const ExperimentProvider = ({ children }: PropsWithChildren) => {
  const [treatments, setTreatments] = React.useState<Record<
    string,
    string
  > | null>({})

  useEffect(() => {
    async function fetchTreatments() {
      await fetch(
        `${process.env.NEXT_PUBLIC_SPOONFLOWER_BASE_URL}/api/experiments/treatments`,
        {
          credentials: 'include', // include cookies so we get the same treatments and for overrides
        }
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.treatments) setTreatments(data.treatments)
        })
        .catch((error) => {
          console.warn('Error fetching experiments:', error)
        })
    }
    fetchTreatments()
  }, [])

  return <Provider.Provider value={treatments}>{children}</Provider.Provider>
}

export const useTreatment = (name: string, value: string) => {
  const context = useContext(Provider)
  if (!context) {
    throw new Error('useTreatment must be used within an ExperimentProvider')
  }
  return context?.[name] ? context[name] === value : null
}

export function Experiment({
  name,
  value,
  children,
  default: isDefault = false,
}: PropsWithChildren<{
  name: string
  value: string
  default?: boolean
}>) {
  const isEnabled = useTreatment(name, value)
  if (isEnabled === null) return null // not in the cohort
  if (!isEnabled && !isDefault) {
    // part of the cohort but not in the experiment
    dispatchEvent(new ExposureEvent(name, 'disabled'))
    return null
  }
  dispatchEvent(new ExposureEvent(name, value))
  return <>{children}</>
}
